import { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@mui/styles';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { IconButton, Paper, useMediaQuery } from '@mui/material';
import { useTheme } from '@classify/react-core-components/styles';

import SearchForm from './SearchForm';
import SelectedPeople from './SelectedPeople';
import SearchContext from './SearchContext';

const useStyles = makeStyles(
  /* istanbul ignore next */ ({ spacing, palette }) => ({
    root: {
      boxSizing: 'border-box',
      position: 'sticky',
      top: 0,
      zIndex: 1,
      padding: spacing(1.5, 1.5, 1.5, 1.5),
    },

    top: {
      height: 40,
      position: 'relative',
    },

    mobileSearchButton: {
      position: 'absolute',
      top: spacing(-0.5),
      right: spacing(-0.5),
      zIndex: 1,
      color: palette.text.main,
    },

    mobileBackButton: {
      position: 'absolute',
      top: spacing(-0.5),
      left: spacing(-0.5),
      zIndex: 1,
      color: palette.text.main,
    },

    resetButton: {
      top: -4,
      left: -4,
      zIndex: 1,
      color: palette.text.main,
    },

    formContainer: {
      boxSizing: 'border-box',
      position: 'absolute',
      width: '100%',
      height: '100%',
      left: 0,
      top: 0,
    },
  })
);

export default function TopBar({
  onCancel = () => {},
  onConfirmSelected = () => {},
  translations,
}) {
  const classes = useStyles();
  const { breakpoints } = useTheme();
  const { onQueryChange, selected, hasEmptyPeopleList } = useContext(SearchContext);
  const isCompactMode = useMediaQuery(breakpoints.down('lg'));
  const [isFieldShown, toggleField] = useState(!isCompactMode);

  const closeButton = (
    <IconButton
      color="inherit"
      className={classes.resetButton}
      onClick={onCancel}
      aria-label={translations.cancelButtonLabel}
      size="large"
    >
      <CloseIcon />
    </IconButton>
  );

  const backButton = (
    <IconButton
      aria-label={translations.toggleSearchFieldLabel({ isFieldShown })}
      color="inherit"
      className={classes.mobileBackButton}
      onClick={() => {
        toggleField((state) => !state);
        onQueryChange('');
      }}
      size="large"
    >
      <ArrowBackIcon />
    </IconButton>
  );

  let leftButton = closeButton;
  if (isCompactMode && isFieldShown) {
    leftButton = backButton;
  }

  useEffect(() => {
    toggleField(!isCompactMode);
  }, [isCompactMode]);

  return (
    <Paper className={classes.root} elevation={2} square>
      <div className={classes.top}>
        {leftButton}

        <div className={classes.formContainer}>
          <SearchForm
            id="people-search"
            translations={translations}
            isCompactMode={isCompactMode}
            isFieldShown={isFieldShown}
          />
        </div>

        {isCompactMode && !isFieldShown && !hasEmptyPeopleList && (
          <IconButton
            aria-label={translations.toggleSearchFieldLabel({ isFieldShown })}
            color="inherit"
            className={classes.mobileSearchButton}
            onClick={() => {
              toggleField((state) => !state);
            }}
            size="large"
          >
            <SearchIcon />
          </IconButton>
        )}
      </div>
      <div role="region" aria-live="polite">
        {selected && (
          /* istanbul ignore next */ <SelectedPeople
            onConfirm={onConfirmSelected}
            translations={translations}
            people={selected}
          />
        )}
      </div>
    </Paper>
  );
}
